import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Send, X, Loader2, Trash2, Plus, MessageSquare, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map friendly tool labels for the "sta lavorando" indicator
const TOOL_LABELS = {
  search_clients: 'Cerco clienti nella CRM...',
  search_contracts: 'Cerco pratiche nella CRM...',
  get_client: 'Apro la scheda cliente...',
  get_contract: 'Apro la pratica...',
  my_commissions_month: 'Calcolo le tue provvigioni...',
  list_pending_renewals: 'Controllo i prossimi rinnovi...',
};

/**
 * Render markdown-style [label](url) links + **bold** + *italic*.
 * Internal links (/foo/bar) are opened via react-router (SPA), external in _blank.
 */
const renderMarkdown = (text, onNavigate) => {
  if (!text) return null;
  const parts = [];
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = linkRe.exec(text)) !== null) {
    if (match.index > last) parts.push({ type: 'text', value: text.slice(last, match.index), key: i++ });
    parts.push({ type: 'link', label: match[1], href: match[2], key: i++ });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last), key: i++ });

  const renderInline = (str) => {
    // bold **text**
    const chunks = [];
    const boldRe = /\*\*([^*]+)\*\*/g;
    let l = 0; let m; let k = 0;
    while ((m = boldRe.exec(str)) !== null) {
      if (m.index > l) chunks.push(<span key={k++}>{str.slice(l, m.index)}</span>);
      chunks.push(<strong key={k++} className="font-semibold">{m[1]}</strong>);
      l = m.index + m[0].length;
    }
    if (l < str.length) chunks.push(<span key={k++}>{str.slice(l)}</span>);
    return chunks;
  };

  return parts.map(p => {
    if (p.type === 'text') return <span key={p.key}>{renderInline(p.value)}</span>;
    const internal = p.href.startsWith('/');
    if (internal) {
      return (
        <button
          key={p.key}
          onClick={() => onNavigate(p.href)}
          className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 underline font-medium"
          data-testid="ai-inline-link"
        >
          {p.label}
        </button>
      );
    }
    return (
      <a key={p.key} href={p.href} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">
        {p.label}
      </a>
    );
  });
};

/**
 * Floating AI Assistant widget available in every page.
 * Uses Gemini 3.5 Flash via SSE streaming; persists chat history in MongoDB.
 */
const AiAssistant = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content, toolLabel?}
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  const handleNavigate = (href) => {
    setOpen(false);
    navigate(href);
  };

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, 20);
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-assistant/sessions`);
      setSessions(res.data || []);
    } catch (e) { /* silent */ }
  };

  const openSession = async (sid) => {
    try {
      const res = await axios.get(`${API_URL}/api/ai-assistant/sessions/${sid}/messages`);
      setSessionId(sid);
      setMessages((res.data || []).map(m => ({ role: m.role, content: m.content })));
      setShowHistory(false);
    } catch (e) { /* silent */ }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setShowHistory(false);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/ai-assistant/sessions/${sid}`);
      setSessions(prev => prev.filter(s => s.id !== sid));
      if (sid === sessionId) newChat();
    } catch (e) { /* silent */ }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: sessionId, message: text }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error('Errore connessione AI');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n\n');
        buf = lines.pop() || '';
        for (const line of lines) {
          const l = line.trim();
          if (!l.startsWith('data:')) continue;
          const payloadStr = l.slice(5).trim();
          if (!payloadStr) continue;
          let payload;
          try { payload = JSON.parse(payloadStr); } catch { continue; }
          if (payload.session_id && !sessionId) setSessionId(payload.session_id);
          if (payload.tool_start) {
            const label = TOOL_LABELS[payload.tool_start] || 'Recupero dati...';
            setMessages(prev => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = { ...last, toolLabel: label };
              }
              return copy;
            });
          }
          if (payload.tool_done) {
            setMessages(prev => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = { ...last, toolLabel: null };
              }
              return copy;
            });
          }
          if (payload.delta) {
            setMessages(prev => {
              const copy = [...prev];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: (copy[copy.length - 1].content || '') + payload.delta, toolLabel: null };
              return copy;
            });
          }
          if (payload.error) {
            setMessages(prev => {
              const copy = [...prev];
              copy[copy.length - 1] = { role: 'assistant', content: `⚠️ Errore: ${payload.error}` };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant' && !last.content) {
          copy[copy.length - 1] = { role: 'assistant', content: '⚠️ Errore di connessione. Riprova.' };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-assistant-open"
          className="fixed bottom-20 lg:bottom-5 right-4 lg:right-5 z-40 flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
          aria-label="Apri Assistente AI"
        >
          <Sparkles className="w-4 h-4" strokeWidth={2.5} />
          <span className="text-sm font-semibold hidden sm:inline">Ada · Assistente AI</span>
          <span className="text-sm font-semibold sm:hidden">Ada AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-50 sm:w-[420px] sm:h-[620px] flex flex-col bg-white sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight">Ada</p>
                <p className="text-[11px] opacity-80">Assistente AI · Gemini</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setShowHistory(s => !s); if (!showHistory) loadSessions(); }}
                title="Storico conversazioni"
                className="p-1.5 rounded-lg hover:bg-white/20"
                data-testid="ai-history-btn"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
              <button
                onClick={newChat}
                title="Nuova chat"
                className="p-1.5 rounded-lg hover:bg-white/20"
                data-testid="ai-new-chat-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/20"
                data-testid="ai-assistant-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* History pane */}
          {showHistory && (
            <div className="border-b border-slate-200 max-h-56 overflow-y-auto bg-slate-50">
              {sessions.length === 0 ? (
                <p className="text-xs text-slate-400 py-6 text-center">Nessuna conversazione salvata</p>
              ) : (
                sessions.map(s => (
                  <div
                    key={s.id}
                    onClick={() => openSession(s.id)}
                    className={`group flex items-center justify-between gap-2 px-4 py-2 cursor-pointer hover:bg-white transition ${s.id === sessionId ? 'bg-white' : ''}`}
                  >
                    <p className="text-xs text-slate-700 truncate flex-1">{s.titolo || 'Conversazione'}</p>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                      title="Elimina"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50" data-testid="ai-messages-list">
            {messages.length === 0 && (
              <div className="text-center pt-8 pb-4">
                <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-100 to-fuchsia-100 items-center justify-center mb-3">
                  <Sparkles className="w-7 h-7 text-indigo-600" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900">Ciao, sono Ada</h3>
                <p className="text-xs text-slate-500 mt-1 px-4">Chiedimi come inserire una pratica, capire le provvigioni, gestire la firma OTP o consigli sulle offerte.</p>
                <div className="mt-4 flex flex-col gap-1.5 px-2">
                  {[
                    'Cerca il cliente Mario',
                    'Quante provvigioni ho maturato questo mese?',
                    'Come firmo un contratto con OTP?',
                    'Quali pratiche hanno rinnovo in scadenza?',
                  ].map(q => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); setTimeout(send, 0); }}
                      className="text-left text-xs px-3 py-1.5 bg-white rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-slate-600 transition"
                      data-testid={`ai-suggest-${q.slice(0,15).replace(/\s+/g,'-')}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-br-md whitespace-pre-wrap'
                    : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'
                }`}>
                  {m.role === 'user' ? (
                    m.content
                  ) : m.toolLabel ? (
                    <span className="inline-flex items-center gap-2 text-slate-500 text-xs italic">
                      <Search className="w-3.5 h-3.5 animate-pulse text-indigo-500" />
                      {m.toolLabel}
                    </span>
                  ) : m.content ? (
                    <span className="whitespace-pre-wrap">{renderMarkdown(m.content, handleNavigate)}</span>
                  ) : (streaming && i === messages.length - 1 ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> : '')}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 px-3 py-3 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrivi ad Ada..."
                rows={1}
                disabled={streaming}
                data-testid="ai-input"
                className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 disabled:opacity-50 max-h-32"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="ai-send-btn"
                className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg transition"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">Ada puo' commettere errori. Verifica sempre importi e procedure.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default AiAssistant;
